/**
 * What the API accepts in a single cell, and the clamp that keeps writes inside it.
 *
 * `MAX_CELL_VALUE_LENGTH` is RE-EXPORTED from the shared lib rather than declared
 * here, because the API refuses against that same constant — a local literal
 * drifting upward turns every long paste into a 400 the client cannot predict.
 * The other two caps are still mirrored from the backend: `TaxonomyFieldValueDto`
 * for a schema code, the list-row validator's `MAX_CELL_LENGTH` for a list cell.
 */
import { MAX_CELL_VALUE_LENGTH } from '@inference.engine/taxonomy/web';
import type { TaxonomyFieldValue } from '../model/types';

export { MAX_CELL_VALUE_LENGTH };
export const MAX_CELL_CODE_LENGTH = 64;
export const MAX_LIST_CELL_LENGTH = 1000;

/**
 * Truncates to `max` UTF-16 code units without splitting a surrogate pair.
 *
 * Code units rather than code points because the list validator compares raw
 * `.length`; dropping the trailing lone high surrogate costs at most one
 * character and avoids emitting a string that no longer survives UTF-8.
 */
export function clampToLength(v: string, max: number): string {
  if (v.length <= max) return v;
  const cut = v.slice(0, max);
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
}

export function clampFieldValue(sub: 'value' | 'code', v: string): string {
  return clampToLength(v, sub === 'value' ? MAX_CELL_VALUE_LENGTH : MAX_CELL_CODE_LENGTH);
}

/** Clamps a whole set of values, for the paths that replace them wholesale. */
export function clampFieldValues(values: TaxonomyFieldValue[]): TaxonomyFieldValue[] {
  return values.map((v) => ({
    ...v,
    value: clampFieldValue('value', v.value),
    code: clampFieldValue('code', v.code),
  }));
}
